import { useCallback, useMemo, useEffect } from "react";
import {
  cn,
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerFooter,
} from "@hireart/component-library";
import PropTypes from "prop-types";
import {
  ShiftShape,
  ProjectOptionShape,
  TimezoneShape,
  isWeekReadOnly,
  getValidationErrorMessages,
} from "common/utils/timesheet";
import { DRAWER_TITLE_ID } from "./constants";
import { useIsMobile } from "./hooks/useIsMobile";
import { useShiftState } from "./hooks/useShiftState";
import { ShiftHeader, ShiftContent, ShiftFooter } from "./sub-components";

export const ShiftDetail = ({
  shift,
  shiftDate,
  dayTotalHours = 0,
  weekStatus = "draft",
  projectOptions = [],
  timezones = [],
  onSave,
  children,
  isSaving = false,
  isOpen = false,
  onOpenChange,
  overrideReasonRequired = false,
}) => {
  const isMobile = useIsMobile();
  const isReadOnly = isWeekReadOnly(weekStatus);

  const {
    editingShift,
    selectedProjectToken,
    updateShift,
    updateShiftTime,
    updateBreak,
    addBreak,
    removeBreak,
    updateProject,
    updateTimezone,
    getCurrentTimezone,
    resetShift,
  } = useShiftState({
    initialShift: shift,
    shiftDate,
    isReadOnly,
  });

  useEffect(() => {
    if (isOpen) {
      resetShift();
    }
  }, [isOpen, resetShift]);

  const validationErrors = useMemo(
    () => getValidationErrorMessages(editingShift),
    [editingShift]
  );

  const handleSave = useCallback(() => {
    onSave(editingShift);
  }, [onSave, editingShift]);

  const handleStartTimeChange = useCallback(
    (time) => updateShiftTime("startTime", time),
    [updateShiftTime]
  );

  const handleEndTimeChange = useCallback(
    (time) => updateShiftTime("endTime", time),
    [updateShiftTime]
  );

  const handleBreakChange = useCallback(
    (breakIndex, field, value) => {
      updateBreak(breakIndex, field, value);
    },
    [updateBreak]
  );

  const handleNotesChange = useCallback(
    (notes) =>
      updateShift({
        notes,
      }),
    [updateShift]
  );

  const handleOverrideNotesChange = useCallback(
    (overrideNotes) =>
      updateShift({
        overrideNotes,
      }),
    [updateShift]
  );

  const handleReferenceNumberChange = useCallback(
    (referenceNumber) =>
      updateShift({
        referenceNumber,
      }),
    [updateShift]
  );

  return (
    <>
      <span className="contents" onClick={() => onOpenChange(true)}>
        {children}
      </span>
      <Drawer
        aria-labelledby={DRAWER_TITLE_ID}
        className={cn(
          "body-1",
          isMobile
            ? "bottom-[env(safe-area-inset-bottom)] h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-6rem)] max-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-6rem)] min-h-[60vh] bg-neutral-10"
            : "h-screen w-full max-w-xl bg-neutral-10"
        )}
        isOpen={isOpen}
        onClose={() => onOpenChange(false)}
        position={isMobile ? "bottom" : "right"}
      >
        <DrawerHeader className="shrink-0 gap-4 p-16">
          <ShiftHeader
            shift={isReadOnly ? shift : editingShift}
            shiftDate={shiftDate}
            weekStatus={weekStatus}
            titleId={DRAWER_TITLE_ID}
          />
        </DrawerHeader>

        <DrawerContent className="flex flex-1 flex-col overflow-hidden">
          <ShiftContent
            shift={shift}
            editingShift={editingShift}
            shiftDate={shiftDate}
            dayTotalHours={dayTotalHours}
            selectedProjectToken={selectedProjectToken}
            projectOptions={projectOptions}
            timezones={timezones}
            validationErrors={validationErrors}
            isReadOnly={isReadOnly}
            onProjectChange={updateProject}
            onStartTimeChange={handleStartTimeChange}
            onEndTimeChange={handleEndTimeChange}
            onTimezoneChange={updateTimezone}
            onBreakChange={handleBreakChange}
            onRemoveBreak={removeBreak}
            onAddBreak={addBreak}
            onNotesChange={handleNotesChange}
            onReferenceNumberChange={handleReferenceNumberChange}
            getCurrentTimezone={getCurrentTimezone}
            overrideReasonRequired={overrideReasonRequired}
            overrideNotes={editingShift.overrideNotes || ""}
            onOverrideNotesChange={handleOverrideNotesChange}
          />
        </DrawerContent>

        <DrawerFooter
          className={cn(
            "shrink-0 border-t border-neutral-20 bg-neutral-10 p-16",
            isMobile && "pb-[calc(40px+env(safe-area-inset-bottom))]"
          )}
        >
          <ShiftFooter
            isReadOnly={isReadOnly}
            onSave={handleSave}
            onClose={() => onOpenChange(false)}
            isSaving={isSaving}
            isSaveDisabled={
              overrideReasonRequired && !editingShift.overrideNotes?.trim()
            }
          />
        </DrawerFooter>
      </Drawer>
    </>
  );
};

ShiftDetail.propTypes = {
  shift: ShiftShape.isRequired,
  shiftDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
    .isRequired,
  dayTotalHours: PropTypes.number,
  weekStatus: PropTypes.string,
  projectOptions: PropTypes.arrayOf(ProjectOptionShape),
  timezones: PropTypes.arrayOf(TimezoneShape),
  onSave: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  isSaving: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func.isRequired,
  overrideReasonRequired: PropTypes.bool,
};
